import { useAuth } from '@/app/providers/AuthProvider';

/**
 * Permission checking hook.
 *
 * Uses `permissionKeys` (string array) returned directly by the backend
 * instead of resolving numeric codes on the frontend.
 * This means the frontend never needs to maintain its own code→key mapping
 * for runtime permission checks — the backend is the single source of truth.
 *
 * The `permissions` (numeric) array is kept for backwards-compat, but all
 * checks here now use the string keys.
 */
export const usePermissions = () => {
    const { user } = useAuth();

    const permissionKeys: string[] = user?.access?.permissionKeys || [];
    const isOwner = user?.access?.isOwner || false;
    const isSuperAdmin = user?.access?.isSuperAdmin || false;

    /**
     * Check if the user has a specific permission by its string key.
     * e.g. hasPermission('flow_builder.view')
     */
    const hasPermission = (permission: string | number): boolean => {
        if (isOwner || isSuperAdmin) return true;
        if (typeof permission === 'number') {
            // Numeric check still supported as fallback (e.g. direct code usage)
            return (user?.access?.permissions || []).includes(permission);
        }
        return permissionKeys.includes(permission);
    };

    /**
     * Returns true if the user has at least one of the given permissions.
     */
    const hasAnyPermission = (requiredPermissions: (string | number)[]): boolean => {
        if (isOwner || isSuperAdmin) return true;
        return requiredPermissions.some(p => hasPermission(p));
    };

    /**
     * Returns true if the user has ALL of the given permissions.
     */
    const hasAllPermissions = (requiredPermissions: (string | number)[]): boolean => {
        if (isOwner || isSuperAdmin) return true;
        return requiredPermissions.every(p => hasPermission(p));
    };

    /**
     * Generic filter for notifications based on user module permissions.
     */
    const filterNotifications = <T extends { type?: string; link?: string }>(notifications: T[]): T[] => {
        if (isOwner || isSuperAdmin) return notifications;
        return notifications.filter((notif) => isNotificationVisible(notif, hasAnyPermission, isOwner || isSuperAdmin));
    };

    return {
        permissions: user?.access?.permissions || [],
        permissionKeys,
        isOwner,
        isSuperAdmin,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        filterNotifications,
    };
};

export interface NotificationModuleRule {
    types?: string[];
    linkPattern?: RegExp;
    requiredPermissions: string[];
}

export const NOTIFICATION_MODULE_RULES: NotificationModuleRule[] = [
    {
        types: ['task_assigned', 'task_due_soon', 'reminder', 'card_moved'],
        linkPattern: /\/sales-crm/i,
        requiredPermissions: ['sales_crm.manage', 'sales_crm.view', 'sales_crm'],
    },
    {
        types: ['conversation_incoming', 'agent_assigned', 'livechat_message'],
        linkPattern: /\/live-chat/i,
        requiredPermissions: ['live_chat.view', 'live_chat.manage', 'live_chat'],
    },
    {
        types: ['appointment_created', 'appointment_reminder', 'appointment_cancelled'],
        linkPattern: /\/appointment/i,
        requiredPermissions: ['appointment.view', 'appointment.manage', 'appointment'],
    },
    {
        types: ['whatsapp_campaign', 'drip_campaign', 'template_status'],
        linkPattern: /\/(whatsapp|campaigns|templates|drip-campaigns)/i,
        requiredPermissions: ['whatsapp_crm.view', 'whatsapp_crm.manage', 'whatsapp_crm'],
    },
    {
        types: ['workflow_executed', 'workflow_error'],
        linkPattern: /\/workflows/i,
        requiredPermissions: ['workflows.view', 'workflows.manage', 'workflows'],
    },
];

export const isNotificationVisible = (
    notif: { type?: string; link?: string },
    hasAnyPermissionFn: (keys: string[]) => boolean,
    isOwnerOrAdmin: boolean
): boolean => {
    if (isOwnerOrAdmin) return true;
    for (const rule of NOTIFICATION_MODULE_RULES) {
        const matchesType = rule.types && notif.type ? rule.types.includes(notif.type) : false;
        const matchesLink = rule.linkPattern && notif.link ? rule.linkPattern.test(notif.link) : false;

        if (matchesType || matchesLink) {
            const hasAccess = hasAnyPermissionFn(rule.requiredPermissions);
            if (!hasAccess) return false;
        }
    }
    return true;
};
